import React, { useEffect, useState, useMemo } from 'react';
import { getConfigInfo } from '../../services/dashboard';
import Render from '../PageContainer/Render';
import Loading from '@/pages/dashboard/components/Loading';
import componentMap from '../../_config/compConfig';
import { mergeWith, isArray, union } from 'lodash-es';
import {
  IMetricComponentProps,
  IConfigRendererProps,
  IConfigCacheData,
  IConfigRow,
  IRemoteConfig,
} from './types';
import classNames from 'classnames';
import { IComponentType } from '../PageContainer/type';
import './index.scss';

/** 配置缓存，避免重复请求 */
const configCache = new Map<string, IConfigCacheData>();
/** 请求锁，防止并发请求 */
const pendingRequests = new Map<string, Promise<IConfigCacheData>>();

/**
 * 标准化配置为数组形式
 * @param config - 配置对象或数组
 * @returns 配置数组
 */
const normalizeConfig = (config: IRemoteConfig | IRemoteConfig[]): IRemoteConfig[] => {
  if (Array.isArray(config)) return config;
  return [config || {}];
};

/**
 * 配置渲染器组件
 * 递归渲染配置列表中的组件
 */
const ConfigRenderer: React.FC<IConfigRendererProps> = ({
  configs,
  data,
  changeData,
  height,
  ...restProps
}) => (
  <>
    {configs?.map((config: IComponentType, index: number) => {
      const {
        name,
        props: componentProps = {},
        children,
        // 组件自身配置的data处理
        data: configData,
        ...restConfig
      } = config || {};

      // 在map中渲染对应组件
      if (name && componentMap[name]) {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        const Component = componentMap[name];
        // 递归渲染 children，与 Render 组件保持一致的处理方式
        const renderedChildren = children?.map((child, childIndex) => (
          <ConfigRenderer
            key={`${child.name}_${childIndex}`}
            configs={[child]}
            data={{
              ...(data || {}),
              ...(configData || {}),
            }}
            changeData={changeData}
            height={height}
            {...restProps}
          />
        ));

        return React.createElement(
          Component,
          {
            key: `${name}_${index}`,
            ...restConfig,
            data: {
              ...(data || {}),
              ...(configData || {}),
            },
            changeData,
            ...restProps,
            ...componentProps,
          },
          renderedChildren,
        );
      }

      // 兜底
      return (
        <div style={{ height: `${height}px` }} key={`render_${index}`}>
          <Render
            key={`render_${index}`}
            config={config}
            data={data}
            changeData={changeData}
            {...restProps}
          />
        </div>
      );
    })}
  </>
);

/**
 * MetricComponent 组件
 * 根据七彩石配置动态渲染指标组件
 */
const MetricComponent: React.FC<IMetricComponentProps> = (props) => {
  const {
    path = '',
    prefixPath = 'dashboard.metric_component',
    config = {},
    data = {},
    changeData,
    height = 300,
    loadingNode,
    componentKey = '',
    className,
    style,
    id,
    ...restProps
  } = props;

  const [remoteConfig, setRemoteConfig] = useState<IRemoteConfig>({});
  const [isLoading, setIsLoading] = useState(true);

  /**
   * 获取七彩石配置
   * @param configPrefixPath - 配置路径前缀
   * @param configPath - 配置路径
   */
  const getRainbowConfig = async (configPrefixPath: string, configPath: string): Promise<void> => {
    const cacheKey = `${configPrefixPath}.${configPath}`;

    // 1. 已有缓存数据
    if (configCache.has(cacheKey)) {
      const cachedData = configCache.get(cacheKey);

      // 根据 componentKey 查找特定配置
      const targetValue = componentKey
        ? cachedData?.rows?.find((item: IConfigRow) => item.key === componentKey)?.value
        : cachedData?.rows?.[0]?.value;

      // 更新状态
      try {
        setRemoteConfig(targetValue ? JSON.parse(targetValue) : {});
      } catch (e) {
        console.error('Failed to parse cached config:', e);
        setRemoteConfig({}); // 使用空配置作为降级
      }
      setIsLoading(false);
      return;
    }

    // 2. 已有相同请求在进行中
    if (pendingRequests.has(cacheKey)) {
      const res = await pendingRequests.get(cacheKey)!;
      const targetConfig = processResponseData(res);
      setRemoteConfig(targetConfig);
      setIsLoading(false);
      return;
    }

    // 3. 需要发起新请求
    const requestPromise = getConfigInfo(configPrefixPath, configPath)
      .then((res) => {
        // 处理响应数据
        const targetConfig = processResponseData(res as IConfigCacheData);

        // 更新缓存
        configCache.set(cacheKey, res as IConfigCacheData);

        // 更新状态
        setRemoteConfig(targetConfig);
        return res as IConfigCacheData;
      })
      .catch((error: Error) => Promise.reject(error))
      .finally(() => {
        pendingRequests.delete(cacheKey);
        setIsLoading(false);
      });

    // 设置请求锁
    pendingRequests.set(cacheKey, requestPromise);
    await requestPromise;
  };

  /**
   * 提取响应数据处理逻辑
   * @param res - 七彩石配置响应数据
   * @returns 解析后的配置对象
   */
  const processResponseData = (res: IConfigCacheData): IRemoteConfig => {
    let targetConfig: IRemoteConfig = {};
    if (res.rows?.length && res.rows.length > 0) {
      const targetRow = componentKey
        ? res.rows.find((row: IConfigRow) => row.key === componentKey)
        : res.rows[0];

      if (targetRow?.value) {
        try {
          targetConfig = JSON.parse(targetRow.value) as IRemoteConfig;
        } catch (e) {
          console.error('Failed to parse remote config:', e);
        }
      }
    }
    return targetConfig;
  };

  useEffect(() => {
    if (!path) return;
    getRainbowConfig(prefixPath, path);
  }, [path, prefixPath]);

  const mergedConfig = useMemo((): IComponentType[] => {
    const localReplaceKeys: string[] = (config as any)?.arrayReplaceKeys || [];
    const remoteReplaceKeys: string[] = (remoteConfig as any)?.arrayReplaceKeys || [];
    const arrayReplaceKeys = new Set(union(localReplaceKeys, remoteReplaceKeys));

    // 初始容器形态：只要 config / remoteConfig 任一为数组，就按数组合并，避免数组被吞或被错误索引化
    const isArrayShape = Array.isArray(config) || Array.isArray(remoteConfig);
    const initialContainer: IRemoteConfig | IRemoteConfig[] = isArrayShape ? [] : {};

    const merged = mergeWith(
      initialContainer,
      remoteConfig,
      config,
      (objValue: unknown, srcValue: unknown, key: string) => {
        // 1. arrayReplaceKeys 配置字段本身不应进入产物（深层、顶层都剔除）
        if (key === 'arrayReplaceKeys') return objValue ?? null;
        // 2. 命中 arrayReplaceKeys 的数组字段：整体替换，而非按索引深度合并
        if (isArray(srcValue) && arrayReplaceKeys.has(key)) return srcValue;
        return undefined;
      },
    );

    // 兜底：customizer 已处理深层，但顶层 arrayReplaceKeys 仍可能从初始容器残留，统一删除
    if (merged && !Array.isArray(merged)) {
      delete (merged as any).arrayReplaceKeys;
    }

    return normalizeConfig(merged) as unknown as IComponentType[];
  }, [remoteConfig, config]);

  if (isLoading) {
    return (
      <div
        className={classNames("dbd-metric-component", className)}
        style={{
          height: `${height}px`,
          width: '100%',
          backgroundColor: 'white',
          borderRadius: '8px',
          ...style,
        }}
        id={id}
      >
        {loadingNode || <Loading />}
      </div>
    );
  }

  return (
    <div className={classNames("dbd-metric-component", className)} style={style} id={id}>
      <ConfigRenderer
        height={height}
        configs={mergedConfig}
        data={data}
        changeData={changeData}
        {...restProps}
      />
    </div>
  );
};

export default MetricComponent;
